// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, EMPTY, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, map, mergeMap, withLatestFrom } from "rxjs/operators"
import * as Actions from "../actions"
import * as TagsSlice from "../slice"
import * as UserContextActions from "../../user-context/actions"
import * as UserContextSlice from "../../user-context/slice"
import { ListRecentTagsResponse, ListRecentTagsRequestSchema } from "generated/legebill/site/tag/v4/tag_pb"
import { throttleSame } from "../../../lib/rxutils"
import type { RootState } from "../../../app/store"
import { RECENT_LIST_TAGS_COUNT } from "../../shared/search-dropdown/config"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"
import { Code, executor, Init, isConnectError, tagService } from "../../../app/rpc"
import { create } from "@bufbuild/protobuf"

type FetchRecentTagsRpc = (request: Init<typeof ListRecentTagsRequestSchema>) => Observable<ListRecentTagsResponse>

export const fetchRecentTags = (fetchRecentTagsRpc: FetchRecentTagsRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.fetchRecentTags.match),
      withLatestFrom(state$),
      // filter out if no current site id
      filter(([, state]) => Boolean(state.userContext.currentSiteId)),
      map(([action, state]) => (
        {
          request: {
            siteId: state.userContext.currentSiteId || "",
            activityDate: state.timeline.activeDate,
            pageSize: RECENT_LIST_TAGS_COUNT,
          },
          swallowFailure: action.payload?.swallowFailure ?? false
        }
      )),
      // throttle for the same active date of the same site
      throttleSame(2000, ({ request: a }, { request: b }) =>
        a.siteId === b.siteId && a.activityDate === b.activityDate
      ),
      mergeMap(({ request, swallowFailure }) =>
        of(request)
          .pipe(
            mergeMap(fetchRecentTagsRpc),
            map(t => t),
            map(TagsSlice.setRecentTags),
            catchError(e => handleError(e, swallowFailure)),
          )),
    )

const fetchRecentTagsRpc = (request: Init<typeof ListRecentTagsRequestSchema>): Observable<ListRecentTagsResponse> =>
  executor(() => tagService.listRecentTags(create(ListRecentTagsRequestSchema, request)))

const handleError = (error: unknown, swallowFailure: boolean | undefined): Observable<Action> => {
  if (isConnectError(error)) {
    const { code } = error
    if (code === Code.PermissionDenied) {
      // clear current site id and re-fetch user (includes sites list) and membership details
      return concat(
        of(UserContextSlice.clearCurrentSiteId()),
        of(UserContextActions.fetchUserDetails()),
      )
    }
  }

  return swallowFailure ?
    EMPTY :
    of(showSnackBar({
      message: t({ id: "msg_fetch_recent_tags_failed", message: "Failed to load recent tags. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
      messageType: "ERROR",
    }))
}

export default fetchRecentTags(fetchRecentTagsRpc)
